// Device Fingerprinting Utility
// Collects device/browser characteristics to create a unique identifier

const getDeviceFingerprint = async () => {
  const components = [];

  // Screen properties
  components.push(`screen:${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`);
  components.push(`availScreen:${window.screen.availWidth}x${window.screen.availHeight}`);
  components.push(`pixelRatio:${window.devicePixelRatio || 1}`);

  // Timezone
  components.push(`timezone:${Intl.DateTimeFormat().resolvedOptions().timeZone}`);
  components.push(`timezoneOffset:${new Date().getTimezoneOffset()}`);

  // Language
  components.push(`language:${navigator.language}`);
  components.push(`languages:${(navigator.languages || []).join(',')}`);

  // Platform & Browser
  components.push(`platform:${navigator.platform}`);
  components.push(`userAgent:${navigator.userAgent}`);
  components.push(`vendor:${navigator.vendor}`);
  components.push(`cookieEnabled:${navigator.cookieEnabled}`);
  components.push(`doNotTrack:${navigator.doNotTrack}`);
  components.push(`hardwareConcurrency:${navigator.hardwareConcurrency || 'unknown'}`);
  components.push(`maxTouchPoints:${navigator.maxTouchPoints || 0}`);

  // Memory (if available)
  if (navigator.deviceMemory) {
    components.push(`deviceMemory:${navigator.deviceMemory}`);
  }

  // Canvas fingerprint
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 200;
    canvas.height = 50;
    
    // Draw some text and shapes
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('AquaPlants+🌿', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('AquaPlants+🌿', 4, 17);
    
    const canvasData = canvas.toDataURL();
    components.push(`canvas:${hashString(canvasData)}`);
  } catch (e) {
    components.push('canvas:unavailable');
  }

  // WebGL fingerprint
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        components.push(`webglVendor:${gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)}`);
        components.push(`webglRenderer:${gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)}`);
      }
      components.push(`webglVersion:${gl.getParameter(gl.VERSION)}`);
    }
  } catch (e) {
    components.push('webgl:unavailable');
  }

  // Audio fingerprint (simplified)
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    components.push(`audioSampleRate:${audioContext.sampleRate}`);
    audioContext.close();
  } catch (e) {
    components.push('audio:unavailable');
  }

  // Installed plugins (limited in modern browsers but still useful)
  if (navigator.plugins) {
    const plugins = Array.from(navigator.plugins)
      .slice(0, 10)
      .map(p => p.name)
      .join(',');
    components.push(`plugins:${plugins || 'none'}`);
  }

  // Local storage test
  try {
    localStorage.setItem('fp_test', '1');
    localStorage.removeItem('fp_test');
    components.push('localStorage:true');
  } catch (e) {
    components.push('localStorage:false');
  }

  // Session storage test
  try {
    sessionStorage.setItem('fp_test', '1');
    sessionStorage.removeItem('fp_test');
    components.push('sessionStorage:true');
  } catch (e) {
    components.push('sessionStorage:false');
  }

  // IndexedDB test
  components.push(`indexedDB:${!!window.indexedDB}`);

  // Create the final fingerprint string and hash it
  const fingerprintString = components.join('|||');
  const fingerprint = await hashStringSHA256(fingerprintString);
  
  return fingerprint;
};

// Simple string hash for canvas (fast, not cryptographic)
const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(16);
};

// SHA-256 hash for the final fingerprint (more secure)
const hashStringSHA256 = async (str) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
};

// Store fingerprint in localStorage for consistency across sessions
const getOrCreateFingerprint = async () => {
  const storageKey = 'app_device_fp';
  
  // Check if we already have a fingerprint stored
  let storedFp = localStorage.getItem(storageKey);
  
  if (storedFp) {
    return storedFp;
  }
  
  // Generate new fingerprint
  const newFp = await getDeviceFingerprint();
  
  // Store it for future use
  try {
    localStorage.setItem(storageKey, newFp);
  } catch (e) {
    // If storage fails, still return the fingerprint
  }
  
  return newFp;
};

export { getDeviceFingerprint, getOrCreateFingerprint };
export default getOrCreateFingerprint;
